import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import Router from './Router';
import ErrorBoundary from './components/ErrorBoundary';
import { LocationModalProvider } from './contexts';
import useAppLoaderStore from './stores/appLoaderStore';
import useAuthStore from './stores/authStore';
import useProviderProfileStore from './stores/providerProfileStore';
import useStaffProfileStore from './stores/staffProfileStore';
import { USER_ROLE } from './types/auth';
import './styles/main.scss';

// Configure QueryClient with better dev experience
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Better DX in development
			retry: process.env.NODE_ENV === 'production',
			// Longer stale time in production
			staleTime: process.env.NODE_ENV === 'production' ? 20000 : 0,
		},
	},
});

// Enable React DevTools profiler in development
if (process.env.NODE_ENV === 'development') {
	const { enablePatches } = await import('immer');
	enablePatches();
}

// App wrapper component
const App = () => {
	const { hideInitialLoader } = useAppLoaderStore();
	const { initializeAuth } = useAuthStore();
	const { fetchProfile: fetchProviderProfile, clearProfile: clearProviderProfile } =
		useProviderProfileStore();
	const { fetchProfile: fetchStaffProfile, clearProfile: clearStaffProfile } =
		useStaffProfileStore();
	const prevAuthRef = useRef<{ isAuthenticated: boolean; role?: string }>({
		isAuthenticated: false,
	});

	useEffect(() => {
		// Initialize auth state from storage (localStorage/sessionStorage)
		initializeAuth();

		// React is now mounted and ready - hide the HTML loader
		// This respects minimum display time automatically
		hideInitialLoader();
	}, [initializeAuth, hideInitialLoader]);

	// Fetch profile at bootstrap based on role, and clear on logout
	const { isAuthenticated, user } = useAuthStore();

	useEffect(() => {
		const prev = prevAuthRef.current;

		if (isAuthenticated && user?.role === USER_ROLE.SALON_OWNER) {
			const { profile } = useProviderProfileStore.getState();
			if (!profile) {
				fetchProviderProfile();
			}
		} else if (isAuthenticated && user?.role === USER_ROLE.STAFF) {
			const { profile } = useStaffProfileStore.getState();
			if (!profile) {
				fetchStaffProfile();
			}
		} else if (prev.isAuthenticated && !isAuthenticated) {
			// User logged out - clear all profile data
			clearProviderProfile();
			clearStaffProfile();
		}

		prevAuthRef.current = { isAuthenticated, role: user?.role };
	}, [
		isAuthenticated,
		user?.role,
		fetchProviderProfile,
		clearProviderProfile,
		fetchStaffProfile,
		clearStaffProfile,
	]);

	return (
		<LocationModalProvider>
			<Router />
			<Toaster
				position="top-right"
				toastOptions={{
					duration: 3000,
					style: {
						background: '#fff',
						color: '#363636',
					},
					success: {
						duration: 3000,
						iconTheme: {
							primary: '#10b981',
							secondary: '#fff',
						},
					},
					error: {
						duration: 4000,
						iconTheme: {
							primary: '#ef4444',
							secondary: '#fff',
						},
					},
				}}
			/>
		</LocationModalProvider>
	);
};

ReactDOM.createRoot(document.getElementById('root')!).render(
	<React.StrictMode>
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<BrowserRouter>
					<App />
				</BrowserRouter>
				{process.env.NODE_ENV === 'development' && (
					<ReactQueryDevtools initialIsOpen={false} />
				)}
			</QueryClientProvider>
		</ErrorBoundary>
	</React.StrictMode>
);
